// client/src/components/command/FetchTab.jsx
// Fetch orders from ShipHero + Validate against Shopify locations.

import React, { useState, useEffect, useCallback } from "react";
import * as api from "../../services/api";
import { useJob, useCommandStats } from "../../hooks/useCommand";
import JobProgress from "./JobProgress";
import CommandStatsCards from "./CommandStatsCards";
import { formatEasternTime, getEasternTimeZoneLabel } from "../../utils/timezone";

// ─── Auto-Fetch Status Indicator ────────────────────────────────────────────
function AutoFetchBanner() {
    const [status, setStatus] = useState(null);
    const load = useCallback(async () => {
        try { setStatus(await api.fetchAutoFetchStatus()); } catch (_) { }
    }, []);
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
    if (!status) return null;

    const fmtTime = (iso) => {
        if (!iso) return "—";
        return `${formatEasternTime(iso, { hour: "2-digit", minute: "2-digit", second: "2-digit" })} ${getEasternTimeZoneLabel()}`;
    };
    const fmtInterval = (ms) => `${Math.round(ms / 60000)}m`;

    const dot = status.running ? "#fbbf24" : status.lastResult === "success" ? "#22c55e" : status.lastResult === "failed" ? "#ef4444" : "#64748b";
    const label = status.running ? "Running..." : status.enabled ? "Active" : "Disabled";

    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            padding: "10px 16px", marginBottom: 16, borderRadius: 8,
            background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.12)",
            fontSize: 12, color: "#94a3b8",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: dot, boxShadow: `0 0 6px ${dot}` }} />
                <strong style={{ color: "#e2e8f0" }}>Auto-Sync: {label}</strong>
            </div>
            <span>Every {fmtInterval(status.intervalMs)}</span>
            {status.lastRunAt && (
                <span>Last: <strong style={{ color: "#e2e8f0" }}>{fmtTime(status.lastRunAt)}</strong></span>
            )}
            {status.nextRunAt && !status.running && (
                <span>Next: <strong style={{ color: "#e2e8f0" }}>{fmtTime(status.nextRunAt)}</strong></span>
            )}
            {status.lastResult === "success" && (
                <span style={{ color: "#22c55e" }}>
                    ✓ {status.lastFetched} fetched, {status.lastValidated} validated
                    {status.lastMismatches > 0 && <span style={{ color: "#ef4444" }}>, {status.lastMismatches} mismatches</span>}
                </span>
            )}
            {status.lastResult === "failed" && (
                <span style={{ color: "#ef4444" }}>✗ {status.error || "Failed"}</span>
            )}
            {status.cycleCount > 0 && (
                <span style={{ marginLeft: "auto", color: "#64748b" }}>Cycle #{status.cycleCount}</span>
            )}
        </div>
    );
}

const s = {
    container: {},
    actions: {
        display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap",
        alignItems: "center",
    },
    btn: (variant) => ({
        padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer",
        fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", gap: "6px",
        background: variant === "primary" ? "linear-gradient(135deg, #8b5cf6, #6366f1)" :
            variant === "success" ? "linear-gradient(135deg, #10b981, #059669)" :
                variant === "danger" ? "linear-gradient(135deg, #ef4444, #dc2626)" :
                    "rgba(255,255,255,0.06)",
        color: variant ? "#fff" : "#94a3b8",
    }),
    info: {
        background: "rgba(139, 92, 246, 0.08)",
        border: "1px solid rgba(139, 92, 246, 0.15)",
        borderRadius: "8px",
        padding: "14px 18px",
        color: "#c4b5fd",
        fontSize: "13px",
        lineHeight: "1.5",
        marginBottom: "16px",
    },
    error: {
        background: "rgba(239, 68, 68, 0.1)",
        borderRadius: "8px",
        padding: "10px 16px",
        color: "#ef4444",
        fontSize: "13px",
        marginBottom: "12px",
    },
};

export default function FetchTab() {
    const [fetchJobId, setFetchJobId] = useState(null);
    const [validateJobId, setValidateJobId] = useState(null);
    const [error, setError] = useState(null);
    const { stats } = useCommandStats(3000);
    const fetchJob = useJob(fetchJobId);
    const validateJob = useJob(validateJobId);

    const handleFetch = async (resume = false, clearExisting = false) => {
        setError(null);
        try {
            const res = await api.startFetchJob(resume, clearExisting);
            setFetchJobId(res.jobId);
        } catch (e) { setError(e.message); }
    };

    const handleValidate = async (reset = false) => {
        setError(null);
        try {
            const res = await api.startValidateJob(reset);
            setValidateJobId(res.jobId);
        } catch (e) { setError(e.message); }
    };

    const handleAbort = async (jobId) => {
        try { await api.abortCommandJob(jobId); } catch (_) { }
    };

    const fetchRunning = fetchJob.job?.status === "running";
    const validateRunning = validateJob.job?.status === "running";

    return (
        <div style={s.container}>
            <div style={s.info}>
                <strong>Step 1:</strong> Fetch all pending orders from ShipHero into the database.<br />
                <strong>Step 2:</strong> Validate each order's fulfillment location against Shopify.<br />
                Default fetch mode uses upserts (keeps existing rows and updates incrementally).<br />
                Orders with mismatched locations will be flagged for cancellation in the Execute tab.
            </div>

            <AutoFetchBanner />

            {error && <div style={s.error}>⚠️ {error}</div>}

            <CommandStatsCards stats={stats} />

            {/* Fetch Actions */}
            <div style={s.actions}>
                <button style={s.btn("primary")} onClick={() => handleFetch(false)} disabled={fetchRunning}>
                    📥 Fetch Orders
                </button>
                {/* Clear + Fetch removed — historical data must be preserved for 30-day retention */}
                {fetchJob.job?.status === "paused" && (
                    <button style={s.btn("default")} onClick={() => handleFetch(true)}>
                        ▶️ Resume Fetch
                    </button>
                )}
                {fetchRunning && (
                    <button style={s.btn("danger")} onClick={() => handleAbort(fetchJobId)}>
                        ⏸️ Pause
                    </button>
                )}
            </div>

            {fetchJob.job && <JobProgress job={fetchJob.job} sseConnected={fetchJob.sseConnected} type="fetch" />}

            {/* Validate Actions */}
            {(stats?.total > 0) && (
                <>
                    <div style={{ ...s.actions, marginTop: "24px" }}>
                        <button style={s.btn("success")} onClick={() => handleValidate(false)} disabled={validateRunning}>
                            🔍 Validate Locations
                        </button>
                        <button style={s.btn("default")} onClick={() => handleValidate(true)} disabled={validateRunning}>
                            🔄 Re-validate All
                        </button>
                        {validateRunning && (
                            <button style={s.btn("danger")} onClick={() => handleAbort(validateJobId)}>
                                ⏸️ Pause
                            </button>
                        )}
                    </div>

                    {validateJob.job && <JobProgress job={validateJob.job} sseConnected={validateJob.sseConnected} type="validate" />}
                </>
            )}
        </div>
    );
}
